/** @format */
import { Query } from '@toucan/common/services/search/types';

import { FilterOption } from '@toucan/client/features/search/config/types';

import { ListingPageProps, ListingPageVariant } from '../index';

/**
 * Trim filter options that have no quantities from the start and end of an
 * array of filter options
 *
 * @format
 * @param {FilterOption[]} options An array of filter options to be trimmed
 * @return {FilterOption[]} An array of trimmed filter options
 */

export function trimFilterOptions(options: FilterOption[]): FilterOption[] {
    let firstQuantityIndexSet = false;
    let firstQuantityIndex = 0;
    let lastQuantityIndex = 0;

    // Find the index of the first and last option that has a quantity
    options.forEach((option, index) => {
        if (option.quantity) {
            if (firstQuantityIndexSet === false) {
                firstQuantityIndexSet = true;
                firstQuantityIndex = index;
            }

            lastQuantityIndex = index;
        }
    });

    return options.slice(firstQuantityIndex, lastQuantityIndex + 1);
}

export function formatSearchFacetNumber(
    number: number | string,
    market: string
): string {
    return number
        .toString()
        .replace(/[\d.]+/g, (value) =>
            Intl.NumberFormat(market).format(parseFloat(value))
        );
}

const validateQueryObject = (queryObject: object) => {
    for (const queryParameter of Object.values(queryObject)) {
        if (typeof queryParameter === 'object') {
            if (!validateQueryObject(queryParameter)) {
                return false;
            }
        }

        const isUndefinedQueryParameter = /undefined/i.test(queryParameter);

        if (isUndefinedQueryParameter) {
            return false;
        }
    }

    return true;
};

/**
 * Determines whether or not the baseQuery has valid parameters
 */
export const validateBaseQuery = (baseQuery: Partial<Query>) => {
    if (Object.keys(baseQuery).length === 0) {
        return false;
    }

    return validateQueryObject(baseQuery);
};

export const getNumFilterSkeletons = ({
    variant,
    showProductNames,
    isAccessoriesFilterEnabled,
}: Pick<
    ListingPageProps,
    'variant' | 'showProductNames' | 'isAccessoriesFilterEnabled'
>) => {
    const numModelFilters = 10;
    const numProductFilters = isAccessoriesFilterEnabled ? 3 : 2;

    if (variant === ListingPageVariant.MODEL || showProductNames)
        return numModelFilters;

    return numProductFilters;
};

export const shouldShowFacetCount = ({
    id,
    variant,
}: {
    id: string;
    variant: ListingPageVariant;
}) => {
    switch (variant) {
        case ListingPageVariant.MODEL:
            return !id.startsWith('product') && !/^model(\w+)Num$/.test(id);
        case ListingPageVariant.PRODUCT:
        default:
            return true;
    }
};
